import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { type ComponentProps, forwardRef } from "react";
import Icon from "~/features/common/components/icons/Icon/index";
import { cn } from "~/features/common/utils/tailwind";
import CustomTooltipWrapper from "../../CustomTooltipWrapper";

const Button = forwardRef<
  HTMLButtonElement,
  ComponentProps<"button"> & {
    condensed?: boolean;
    destructive?: boolean;
    ghost?: boolean;
    isLoading?: boolean;
    primary?: boolean;
    quickHackWidth?: boolean;
    testId?: string;
    toolTip?: string;
    type?: "button" | "submit" | "reset";
    width?: string;
  }
>((props, ref) => {
  const {
    children,
    className,
    condensed,
    destructive = false,
    disabled,
    ghost = false,
    isLoading = false,
    onClick,
    primary = false,
    quickHackWidth = false,
    testId,
    toolTip,
    type = "button",
    width,
    ...otherProps
  } = props;

  const buttonWidth = width || "flex-1";

  return (
    <CustomTooltipWrapper content={toolTip} className={cn(!quickHackWidth && "inline-flex")}>
      <button
        ref={ref}
        className={cn(
          "inline-flex items-center justify-center rounded border border-gray-300 bg-white text-xs font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-default disabled:opacity-50 dark:border-gray-500 dark:bg-black dark:text-gray-300",
          buttonWidth,
          primary ? "bg-primary text-white dark:text-white dark:bg-primary" : "text-gray-700",
          !disabled && !primary && "hover:bg-gray-50 dark:hover:bg-gray-700",
          !disabled && primary && "hover:bg-primary-hover",
          condensed ? "px-1 py-1.5" : "px-2.5 py-1.5",
          ghost && "!border-none !bg-transparent !shadow-none",
          destructive &&
            ghost &&
            "text-red-600 hover:text-red-800 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20 transition-colors",
          destructive &&
            !ghost &&
            "bg-red-500 text-white hover:bg-red-600 dark:bg-red-500 dark:text-white dark:hover:bg-red-600 disabled:bg-gray-300 disabled:text-gray-500 disabled:hover:bg-gray-300",
          className,
        )}
        data-testid={testId}
        disabled={disabled}
        onClick={onClick}
        type={type}
        {...otherProps}>
        {isLoading && (
          <Icon className="mr-1" content={"Loading..."} data-testid="loading-spinner" icon={faSpinner} spin />
        )}
        {children}
      </button>
    </CustomTooltipWrapper>
  );
});

export default Button;
